import { isRouteAccessDenied } from "@/session/route-query";
import { VerifiedViewer } from "@/session/product-session";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useRouter } from "@tanstack/react-router";
import { useActualViewer } from "@softmax/web-features/session/records";
import type { ReactNode } from "react";
import {
  searchStringOf,
  type FrontDoorData,
  type PreviewSearch,
} from "./front-door";
import { getPreviewDetails, getPreviewViewer } from "../preview.functions";

export function FrontDoor({
  data,
  search,
  children,
}: {
  data: FrontDoorData;
  search: PreviewSearch;
  children: (
    data: FrontDoorData,
    refreshPublished: () => Promise<void>,
  ) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const refreshPublished = async () => {
    await Promise.all([
      queryClient.invalidateQueries(
        { queryKey: ["preview", "details"] },
        { throwOnError: true },
      ),
      queryClient.invalidateQueries(
        { queryKey: ["preview", "front-door"] },
        { throwOnError: true },
      ),
    ]);
    await router.invalidate({ sync: true });
  };
  const href = useLocation({ select: (location) => location.href });
  const url = new URL(href, "https://softmax.invalid");
  const version = url.searchParams.get("version");
  const resourceHref = `${url.pathname}${version ? `?${new URLSearchParams({ version })}` : ""}`;
  const actual = useActualViewer();
  const viewerId = actual.data?.subject_id ?? null;
  const details = useQuery({
    queryKey: ["preview", "details", viewerId, resourceHref],
    queryFn: ({ signal }) => getPreviewDetails({ href: resourceHref, signal }),
    throwOnError: isRouteAccessDenied,
    staleTime: 60_000,
    enabled: typeof window !== "undefined",
  });
  const viewer = useQuery({
    queryKey: ["preview", "viewer", viewerId, resourceHref],
    queryFn: ({ signal }) => getPreviewViewer({ href: resourceHref, signal }),
    throwOnError: isRouteAccessDenied,
    staleTime: 30_000,
    enabled:
      typeof window !== "undefined" && actual.isSuccess && actual.data !== null,
  });
  const page = {
    ...data,
    content: details.data ?? data.content,
    doorHref: `${data.doorHref.split("?")[0]}${searchStringOf(search)}`,
  };

  if (details.isError && !details.data) {
    page.content = { ...data.content, detailsState: "unavailable" };
  }

  let body = children(page, refreshPublished);

  if (actual.data && viewer.data?.viewer) {
    const { viewer: authorizer, ...capabilities } = viewer.data;
    const personalized = {
      ...page,
      ...capabilities,
      doorHref: `${capabilities.doorHref.split("?")[0]}${searchStringOf(search)}`,
    };
    body = (
      <VerifiedViewer viewer={authorizer} fallback={body}>
        {children(personalized, refreshPublished)}
      </VerifiedViewer>
    );
  }

  if (page.content.viewer)
    body = <VerifiedViewer viewer={page.content.viewer}>{body}</VerifiedViewer>;

  return (
    <>
      {body}
      {(details.isError || viewer.isError || actual.isError) && (
        <output className="mx-auto block max-w-5xl px-6 py-3 text-sm">
          {details.isError
            ? "Some game details could not be refreshed. "
            : "Your play status could not be refreshed. "}
          <button
            type="button"
            onClick={() => {
              if (details.isError) void details.refetch();

              if (viewer.isError) void viewer.refetch();

              if (actual.isError) void actual.refetch();
            }}
          >
            Try again
          </button>
        </output>
      )}
    </>
  );
}
